import { AddFundsModal } from '@/features/settings/components/add-funds-modal';

import type { ScreeningStatusResponse } from '../api/risk-assessment-wizard';
import type { PaymentFlowStep } from '../hooks/use-payment-flow';

import { DownloadReportModal } from './download-report-modal';
import { EddModal } from './edd-modal';
import { EddUpsellModal } from './edd-upsell-modal';
import { MatchReportModal } from './match-report-modal';
import { NoReportAccessModal } from './no-report-access-modal';
import { RequestDocsModal } from './request-docs-modal';
import { ScreeningModal } from './screening-modal';
import { ScreeningProgressModal } from './screening-progress-modal';

export type { PaymentFlowStep };

interface PaymentFlowModalsProps {
  flowStep: PaymentFlowStep;
  assessmentId: string;
  screeningStatus: ScreeningStatusResponse | undefined;
  isScreeningPending: boolean;
  isEddPending: boolean;
  isProcessing: boolean;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  showAddFundsModal: boolean;
  setShowAddFundsModal: (open: boolean) => void;
  onScreeningProgressComplete: () => void;
  onBackToDashboard: () => void;
  onProceedFromDownload: () => void;
  onMatchReportPurchase: () => void;
  onSkipMatchReport: () => void;
  onEddPurchase: () => void;
  onSkipEddUpsell: () => void;
  onSkipEdd: () => void;
  onDocRequest: (email: string, documentTypes: string[], otherDescription?: string) => void;
  onSkipDocRequest: () => void;
  onViewReport: () => void;
  onGoBackToPurchase: () => void;
}

export function PaymentFlowModals({
  flowStep,
  assessmentId,
  screeningStatus,
  isScreeningPending,
  isEddPending,
  isProcessing,
  riskLevel,
  showAddFundsModal,
  setShowAddFundsModal,
  onScreeningProgressComplete,
  onBackToDashboard,
  onProceedFromDownload,
  onMatchReportPurchase,
  onSkipMatchReport,
  onEddPurchase,
  onSkipEddUpsell,
  onSkipEdd,
  onDocRequest,
  onSkipDocRequest,
  onViewReport,
  onGoBackToPurchase,
}: PaymentFlowModalsProps) {
  return (
    <>
      <ScreeningProgressModal
        open={flowStep === 'SCREENING_PROGRESS'}
        screeningStatus={screeningStatus}
        isInitialScreening={isScreeningPending}
        onBackToDashboard={onBackToDashboard}
        onComplete={onScreeningProgressComplete}
      />

      <DownloadReportModal
        open={flowStep === 'DOWNLOAD_REPORT'}
        assessmentId={assessmentId}
        onSkip={onProceedFromDownload}
      />

      <MatchReportModal
        open={flowStep === 'MATCH_REPORT'}
        riskLevel={riskLevel}
        onPurchase={onMatchReportPurchase}
        onSkip={onSkipMatchReport}
        onAddFunds={() => setShowAddFundsModal(true)}
        isPurchasing={isProcessing}
      />

      <EddUpsellModal
        open={flowStep === 'EDD_UPSELL'}
        onPurchase={onEddPurchase}
        onSkip={onSkipEddUpsell}
        onAddFunds={() => setShowAddFundsModal(true)}
        isPurchasing={isProcessing}
      />

      <EddModal
        open={flowStep === 'EDD'}
        onPurchase={onEddPurchase}
        onSkip={onSkipEdd}
        onAddFunds={() => setShowAddFundsModal(true)}
        isPurchasing={isProcessing}
      />

      <RequestDocsModal
        open={flowStep === 'REQUEST_DOCS'}
        clientEmail=""
        onPurchase={onDocRequest}
        onSkip={onSkipDocRequest}
        onAddFunds={() => setShowAddFundsModal(true)}
        isPurchasing={isProcessing}
      />

      <ScreeningModal
        open={flowStep === 'REPORT_GENERATING'}
        onOpenChange={() => undefined}
        isScreening={isEddPending}
        screeningStatus={screeningStatus}
        onViewReport={onViewReport}
        onBackToDashboard={onBackToDashboard}
      />

      <NoReportAccessModal
        open={flowStep === 'NO_ACCESS'}
        onOpenChange={() => undefined}
        onGoBack={onGoBackToPurchase}
        onReturnToDashboard={onBackToDashboard}
      />

      <AddFundsModal open={showAddFundsModal} onOpenChange={setShowAddFundsModal} />
    </>
  );
}
